import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Link } from "@tanstack/react-router";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { ArchivedNotice } from "@/components/marketing/ArchivedNotice";

export const Route = createFileRoute("/archive/pricing")({
  component: PricingPage,
});

const PRINCIPLES = [
  {
    title: "No per-user fees",
    copy: "No per-user fees, ever. No adoption barriers, no per-seat decisions. Invite your whole team without budget anxiety.",
  },
  {
    title: "Pay for the data you use",
    copy: "Usage-based pricing across every product - pay for data, not per user. You pay only for the data you store and use.",
  },
  {
    title: "Start free",
    copy: "With MyApp, your first 1,500 records are free. Move your data out of spreadsheets and into a real database before you pay anything.",
  },
];

const PRODUCT_NOTES = [
  {
    title: "MyApp",
    copy: "Your first 1,500 records are free. No per-user fees - just pay for the data you store. Invite your whole team without budget anxiety.",
    to: "/products/myapp",
    linkLabel: "Explore MyApp",
  },
  {
    title: "Knowledge Base",
    copy: "No per-user pricing. Pay for the knowledge base, not the number of people who need answers.",
    to: "/products/knowledge-base",
    linkLabel: "Explore Knowledge Base",
  },
];

const FAQ = [
  {
    question: "Do you charge per user?",
    answer:
      "No. Larasoft has no per-user fees, ever. Pricing is usage-based across every product - you pay for the data you use, not the number of people who use it. Invite your whole team without budget anxiety.",
  },
  {
    question: "What happens when we grow?",
    answer:
      "Because pricing is based on the data you use rather than seats, adding people never changes your bill. A 10-person team and a 100-person team pay the same for the same data - there are no adoption barriers and no per-seat decisions.",
  },
  {
    question: "Is there a free way to start?",
    answer:
      "Yes. With MyApp, your first 1,500 records are free, so you can move data out of spreadsheets and into a real database before you pay anything.",
  },
  {
    question: "Does Knowledge Base have per-user pricing?",
    answer:
      "No. Knowledge Base has no per-user pricing - you pay for the knowledge base, not the number of people who need answers.",
  },
  {
    question: "Do you offer special pricing for nonprofits?",
    answer:
      "Yes. We are passionate about empowering NGOs and nonprofit organisations. Special pricing is available so nonprofits can access the same enterprise-grade tools.",
  },
  {
    question: "How do I get an exact price?",
    answer:
      "Pricing is usage-based, so the best way to get an exact figure is to request a quote. Tell us about your systems and data volumes and we'll put together pricing for your organisation.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

function PricingPage() {
  return (
    <MarketingLayout>
      <Helmet>
        {/*
          Archived pricing page, kept for reference while parts are mined for other
          pages. Noindexed so it cannot compete with the live pricing page - the two
          describe different commercial models, and a prospect landing here would be
          reading something we no longer offer.

          Deliberately NO canonical: pairing noindex with a canonical pointing
          at another page sends contradictory instructions. Noindex alone is the
          clear signal, and this page is absent from the sitemap.
        */}
        <meta name="robots" content="noindex,nofollow" />
        <title>Pricing | Larasoft</title>
        <meta
          name="description"
          content="Simple, usage-based pricing across every Larasoft product. Pay for data, not per user - no per-user fees, no adoption barriers. MyApp's first 1,500 records are free."
        />
        <meta property="og:title" content="Pricing | Larasoft" />
        <meta
          property="og:description"
          content="Simple, usage-based pricing across every Larasoft product. Pay for data, not per user - no per-user fees, no adoption barriers."
        />
        <meta property="og:url" content="https://larasoft.global/archive/pricing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(FAQ_SCHEMA)}
        </script>
      </Helmet>

      <ArchivedNotice
        what="pricing page"
        to={"/pricing"}
        currentLabel="current pricing"
      />

      {/* Hero */}
      <Section containerClassName="max-w-3xl space-y-6 text-center" className="py-20">
        <Badge className="mx-auto w-max" variant="info">
          Pricing
        </Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
          Simple, usage-based pricing
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-slate-600">
          Usage-based pricing across every product -{" "}
          <span className="font-semibold text-brand-blue-dark">
            pay for data, not per user
          </span>
          . No adoption barriers, no per-seat decisions.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/contact">Request a quote</Link>
          </Button>
          <Button variant="outline" asChild>
            <a href="#faq">Read the FAQ</a>
          </Button>
        </div>
      </Section>

      {/* Three principle cards */}
      <Section containerClassName="space-y-8" className="py-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {PRINCIPLES.map((principle) => (
            <Card key={principle.title}>
              <CardContent className="space-y-3">
                <CardTitle>{principle.title}</CardTitle>
                <CardDescription>{principle.copy}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* How it works - relative worked example */}
      <Section containerClassName="max-w-3xl space-y-6" className="py-16">
        <div className="space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold">How it works</h2>
          <p className="text-sm text-slate-600">
            Pricing follows your data, not your headcount.
          </p>
        </div>
        <div className="space-y-4 text-slate-600">
          <p>
            Every Larasoft product is priced on the data you use. There are no
            per-user fees and no per-seat decisions - so the size of your team
            never changes what you pay.
          </p>
          <Card>
            <CardContent className="space-y-3">
              <CardTitle>A worked example</CardTitle>
              <CardDescription>
                Take a 10-person team and a 100-person team working with the same
                data. They pay the same. Because you pay for data rather than
                seats, inviting ten times as many people to collaborate costs you
                nothing extra. You can roll a tool out to your whole organisation
                without budget anxiety.
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Product notes */}
      <Section containerClassName="space-y-8" className="py-16">
        <div className="space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold">By product</h2>
          <p className="text-sm text-slate-600">
            The same usage-based principle, applied to each product.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {PRODUCT_NOTES.map((note) => (
            <Card key={note.title}>
              <CardContent className="space-y-3">
                <CardTitle>{note.title}</CardTitle>
                <CardDescription>{note.copy}</CardDescription>
                <Link
                  to={note.to}
                  className="inline-block text-sm font-semibold text-brand-blue-medium hover:underline"
                >
                  {note.linkLabel}
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" containerClassName="max-w-3xl space-y-8" className="py-16">
        <div className="space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold">
            Frequently asked questions
          </h2>
          <p className="text-sm text-slate-600">
            Everything you need to know about how Larasoft is priced.
          </p>
        </div>
        <div className="space-y-4">
          {FAQ.map((item) => (
            <Card key={item.question}>
              <CardContent className="space-y-2">
                <CardTitle className="text-base">{item.question}</CardTitle>
                <CardDescription>{item.answer}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* CTA panel */}
      <Section
        containerClassName="text-center space-y-6"
        className="py-16 bg-gradient-to-r from-brand-blue-dark via-brand-blue-medium to-brand-blue-dark text-white"
      >
        <h2 className="text-2xl sm:text-3xl font-bold">
          Ready for pricing tailored to your data?
        </h2>
        <p className="mx-auto max-w-xl text-white/85">
          Tell us about your systems and data volumes and we'll put together
          usage-based pricing for your organisation.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            asChild
            className="bg-white text-brand-blue-dark hover:bg-white/90"
          >
            <Link to="/contact">Request a quote</Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            <Link to="/contact">Speak to us</Link>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  );
}
